import { useState } from "react";

import { Column, Row, Text } from "@/components";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Button } from "@ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";

import { useUpdateSetDrugAndBackground } from "@/hooks/updateSets/useUpdateSetDrugAndBackground";
import { DrugScreenStatus } from "@/types";

const DRUG_SCREEN_ORDER = [
  DrugScreenStatus.WILL_PASS,
  DrugScreenStatus.WILL_PASS_EX_THC,
  DrugScreenStatus.WILL_NOT_PASS,
];
const drugScreenToLabel: Record<DrugScreenStatus, string> = {
  [DrugScreenStatus.WILL_NOT_PASS]: "Won't pass",
  [DrugScreenStatus.WILL_PASS]: "Will pass",
  [DrugScreenStatus.WILL_PASS_EX_THC]: "Will pass (ex THC)",
};

// The select speaks in the column's own terms — "has a criminal record" — rather
// than in pass/fail, which inverts depending on who is reading it.
const CRIMINAL_RECORD_YES = "YES";
const CRIMINAL_RECORD_NO = "NO";
const CRIMINAL_RECORD_OPTIONS = [
  { label: "Yes", value: CRIMINAL_RECORD_YES },
  { label: "No", value: CRIMINAL_RECORD_NO },
];
// Neither field has been established for every candidate. Unset stays unset
// unless the staffer picks something.
const UNKNOWN_PLACEHOLDER = "—";

export const DrugAndBackgroundEditor = ({
  drugScreenStatus,
  hasCriminalRecord,
  onDone,
  stafferId,
  userId,
}: {
  drugScreenStatus: string | null;
  hasCriminalRecord: boolean | null;
  onDone: () => void;
  stafferId: number;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Update
  const { isPending, mutate: saveDrugAndBackground } =
    useUpdateSetDrugAndBackground();

  /* React Hooks */
  const [drugScreenDraft, setDrugScreenDraft] = useState(
    drugScreenStatus ?? undefined,
  );
  const [criminalRecordDraft, setCriminalRecordDraft] = useState(() => {
    if (hasCriminalRecord == null) return undefined;
    return hasCriminalRecord ? CRIMINAL_RECORD_YES : CRIMINAL_RECORD_NO;
  });

  /* Handlers */
  const handleDone = () => {
    const draftCriminalRecord =
      criminalRecordDraft == null
        ? undefined
        : criminalRecordDraft === CRIMINAL_RECORD_YES;
    // Only fields the staffer actually changed are sent, so an untouched field
    // does not get an EDIT row asserting the value it already held.
    const data = {
      ...(drugScreenDraft != null &&
        drugScreenDraft !== drugScreenStatus && {
          drugScreenStatus: drugScreenDraft as DrugScreenStatus,
        }),
      ...(draftCriminalRecord != null &&
        draftCriminalRecord !== hasCriminalRecord && {
          hasCriminalRecord: draftCriminalRecord,
        }),
    };

    if (!Object.keys(data).length) {
      onDone();
      return;
    }

    saveDrugAndBackground({ data, stafferId, userId }, { onSuccess: onDone });
  };

  /* Render */
  const render = () => {
    return (
      <Row className="flex-wrap items-end gap-3">
        <Column className="gap-1">
          <Text className="font-plex-mono text-[9px] font-medium uppercase tracking-wider text-slate-400">
            Drug screen
          </Text>
          <Select
            disabled={isPending}
            onValueChange={setDrugScreenDraft}
            value={drugScreenDraft}
          >
            <SelectTrigger className="h-7 w-40 text-sm">
              <SelectValue placeholder={UNKNOWN_PLACEHOLDER} />
            </SelectTrigger>
            <SelectContent>
              {DRUG_SCREEN_ORDER.map((status) => (
                <SelectItem key={status} value={status}>
                  {drugScreenToLabel[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Column>
        <Column className="gap-1">
          <Text className="font-plex-mono text-[9px] font-medium uppercase tracking-wider text-slate-400">
            Criminal record
          </Text>
          <Select
            disabled={isPending}
            onValueChange={setCriminalRecordDraft}
            value={criminalRecordDraft}
          >
            <SelectTrigger className="h-7 w-24 text-sm">
              <SelectValue placeholder={UNKNOWN_PLACEHOLDER} />
            </SelectTrigger>
            <SelectContent>
              {CRIMINAL_RECORD_OPTIONS.map(({ label, value }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Column>
        <Button
          className="h-7 px-3 text-xs"
          disabled={isPending}
          onClick={handleDone}
          size="sm"
          type="button"
        >
          {isPending ? (
            <Row className="items-center gap-1.5">
              <LoadingIndicator gearColor="white" variant="small" />
              Saving
            </Row>
          ) : (
            "Done"
          )}
        </Button>
      </Row>
    );
  };

  return render();
};
